import type { FocusableOption, FocusOrigin } from '@angular/cdk/a11y';
import { FocusMonitor } from '@angular/cdk/a11y';
import type { OnDestroy, AfterViewInit } from '@angular/core';
import { HostBinding } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
  Optional,
  Inject,
  Input,
  booleanAttribute,
} from '@angular/core';
import { MatRipple } from '@angular/material/core';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import type { CanColor, CanDisable, CanDisableRipple } from '../core/common';
import { mixinDisableRipple, mixinColor, mixinDisabled } from '../core/common';

/**
 * List of classes to add to button instances based on host attributes to
 * style as different variants.
 */
const BUTTON_HOST_ATTRIBUTES = [
  'app-mat-button',
  'app-mat-flat-button',
  'app-mat-icon-button',
  'app-mat-raised-button',
  'app-mat-stroked-button',
];

// Boilerplate for applying mixins to button.
const _MatButtonBase = mixinColor(
  mixinDisabled(
    mixinDisableRipple(
      class {
        constructor(public _elementRef: ElementRef) {}
      },
    ),
  ),
);

/**
 * Material design button.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: `button[app-mat-button], button[app-mat-raised-button], button[app-mat-icon-button],
             button[app-mat-stroked-button],
             button[app-mat-flat-button]`,
  exportAs: 'matButton',
  templateUrl: 'button.component.html',
  styleUrls: ['button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatButtonComponent
  extends _MatButtonBase
  implements AfterViewInit, OnDestroy, CanDisable, CanColor, CanDisableRipple, FocusableOption
{
  /** Reference to the MatRipple instance of the button. */
  @ViewChild(MatRipple) ripple: MatRipple;

  // Add a class for disabled button styling instead of using attribute
  // selector or pseudo-selector.  This allows users to create focusable
  // disabled buttons without recreating the styles.
  @HostBinding('class.mat-button-disabled')
  @Input({
    transform: booleanAttribute,
  })
  override disabled = super.disabled;

  @Input() override disableRipple = super.disableRipple;
  @Input() override color = super.color;

  @HostBinding('class') readonly hostClass = 'mat-focus-indicator';

  @HostBinding('attr.disabled')
  get hostDisabled() {
    return this.disabled || null;
  }

  @HostBinding('class._mat-animation-noopable')
  get isAnimationNoopable() {
    return this._animationMode === 'NoopAnimations';
  }

  /** Whether the button is icon button. */
  readonly isIconButton: boolean = this._hasHostAttributes('app-mat-icon-button');

  constructor(
    elementRef: ElementRef,
    private _focusMonitor: FocusMonitor,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) public _animationMode: string,
  ) {
    super(elementRef);

    // For each of the variant selectors that is present in the button's host
    // attributes, add the correct corresponding class.
    for (const attr of BUTTON_HOST_ATTRIBUTES) {
      if (this._hasHostAttributes(attr)) {
        // remove "app-" from the attr
        const className = attr.replace(/^app-/, '');

        this._getHostElement().classList.add(className);
      }
    }

    // Add a class that applies to all buttons. This makes it easier to target if somebody
    // wants to target all Material buttons. We do it here rather than `host` to ensure that
    // the class is applied to derived classes.
    elementRef.nativeElement.classList.add('mat-button-base');
  }

  ngAfterViewInit() {
    this._focusMonitor.monitor(this._elementRef, true);
  }

  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  /** Focuses the button. */
  focus(origin?: FocusOrigin, options?: FocusOptions): void {
    if (origin) {
      this._focusMonitor.focusVia(this._getHostElement(), origin, options);
    } else {
      this._getHostElement().focus(options);
    }
  }

  _getHostElement(): HTMLElement {
    return this._elementRef.nativeElement;
  }

  _isRippleDisabled() {
    return this.disableRipple || this.disabled;
  }

  /** Gets whether the button has one of the given attributes. */
  _hasHostAttributes(...attributes: string[]) {
    return attributes.some((attribute) => this._getHostElement().hasAttribute(attribute));
  }
}
